/**
 * IBAS Experience data — the single source of truth for the /experience page
 * (Campus Thalwil · Student Achievement Rates · Andragogical Principles ·
 * Internship · Alumni · Gallery).
 *
 * SOURCING (ibas-brand skill, hard rule): every institutional fact here is drawn
 * ONLY from the branding folder, https://ibas.edu.eu, or the user — nothing is
 * invented. Where a figure is not verified it is LEFT OUT (never guessed).
 * Key sources:
 *   • Campus copy + facilities:  https://ibas.edu.eu/ibas-experience/campus/
 *   • ACBSP achievement figures: https://ibas.edu.eu/student-support/students-achievement-rates/
 *   • Andragogy (VERBATIM ★):    branding / "IBAS Website Requirements from
 *                                Accreditations.PDF" — Appendix C (accreditation-
 *                                mandated; wording must NOT be altered).
 *   • Internship pathways:       https://ibas.edu.eu/ibas-experience/internship/
 *   • Alumni copy:               https://ibas.edu.eu/ibas-experience/alumni/
 *   • Alumni spotlight facts/photos: https://ibas.edu.eu/uncategorized/thank-you-
 *                                to-ibas-alumna-sirithakorn-srinitioj-nickname-
 *                                chisa-for-joining-us-as-a-guest-speaker/
 * Images are real IBAS media-library assets (apps/web/public/media). Every
 * claim carries year/scope where practical; no guaranteed-outcome language is
 * used (internship placements are NOT guaranteed).
 */

import {
  Award,
  BookOpen,
  Brain,
  Building2,
  Bus,
  Compass,
  GraduationCap,
  Handshake,
  Home,
  Laptop,
  Lightbulb,
  MapPin,
  Presentation,
  Sparkles,
  Target,
  Train,
  TrendingUp,
  Users,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
 * 1 · CAMPUS — THALWIL   (source: /ibas-experience/campus/)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const CAMPUS_ADDRESS = 'Gewerbestrasse 14, 8800 Thalwil, Switzerland';

/** A single space on the Thalwil floor. */
export interface CampusFacility {
  name: string;
  description: string;
  Icon: LucideIcon;
}

/** The on-site floor occupies an entire building level in Thalwil. */
export const CAMPUS_FACILITIES: readonly CampusFacility[] = [
  {
    name: 'Traditional classroom',
    description: 'A dedicated room for lectures and structured academic sessions.',
    Icon: Presentation,
  },
  {
    name: 'Open collaborative classroom',
    description: 'An open layout designed to encourage collaboration and group work.',
    Icon: Users,
  },
  {
    name: 'Two integrated lounges',
    description: 'Two lounge areas built into the floor for discussion and teamwork.',
    Icon: Sparkles,
  },
  {
    name: 'Student lounge',
    description: 'An informal space for conversations between sessions.',
    Icon: Home,
  },
  {
    name: 'Student office',
    description: 'On-site administrative and academic support.',
    Icon: Building2,
  },
];

/** Practical facts about reaching and living around the campus. */
export interface CampusNote {
  label: string;
  value: string;
  Icon: LucideIcon;
}

export const CAMPUS_NOTES: readonly CampusNote[] = [
  {
    label: 'Getting here',
    value:
      'Thalwil has a major train station with direct connections to Zurich and the surrounding regions.',
    Icon: Train,
  },
  {
    label: 'From the station',
    value: 'Public buses run every 30 minutes, with a stop a two-minute walk from campus.',
    Icon: Bus,
  },
  {
    label: 'Around campus',
    value:
      'A café around the corner, walking-distance restaurants, a Jumbo Center with a Coop supermarket, food trucks, plus a public library and indoor pool nearby.',
    Icon: UtensilsCrossed,
  },
  {
    label: 'Accommodation',
    value:
      'IBAS does not have its own student housing, but assists students in locating rooms in surrounding student dormitories.',
    Icon: Home,
  },
];

/** Opening hours (source: campus page). */
export const CAMPUS_HOURS = {
  office: 'Monday & Thursday, 09:30–17:00 (excluding holidays)',
  campus: 'According to the class schedule',
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
 * 2 · STUDENT ACHIEVEMENT RATES ★  (ACBSP-mandated; source: /student-support/
 *     students-achievement-rates/). Figures are institution-reported, 2021–2025.
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Headline count-up figures (most recent reporting year: 2025). */
export interface AchievementStat {
  /** Numeric value for the count-up. */
  value: number;
  suffix?: string;
  decimals?: number;
  label: string;
  /** Year/scope caption — required by the Claims Rule. */
  caption: string;
  Icon: LucideIcon;
}

export const ACHIEVEMENT_HEADLINE: readonly AchievementStat[] = [
  {
    value: 97.3,
    suffix: '%',
    decimals: 1,
    label: 'MBA retention rate',
    caption: 'ACBSP-accredited MBA · 2025',
    Icon: Award,
  },
  {
    value: 94.4,
    suffix: '%',
    decimals: 1,
    label: 'DBA retention rate',
    caption: 'ACBSP-accredited DBA · 2025',
    Icon: Award,
  },
  {
    value: 99.7,
    suffix: '%',
    decimals: 1,
    label: 'Institution-wide retention',
    caption: 'All students · 2025',
    Icon: TrendingUp,
  },
  {
    value: 1275,
    label: 'Graduates in 2025',
    caption: 'Institution-wide · 2025',
    Icon: GraduationCap,
  },
];

/** One year of retention figures. */
export interface RetentionRow {
  year: string;
  /** ACBSP MBA retention %, e.g. 97.3. */
  mba: number;
  /** ACBSP DBA retention %, e.g. 94.4. */
  dba: number;
  /** Institution-wide (all students) retention %. */
  institution: number;
}

export const RETENTION_SERIES: readonly RetentionRow[] = [
  { year: '2021', mba: 100.0, dba: 50.0, institution: 82.9 },
  { year: '2022', mba: 83.7, dba: 100.0, institution: 96.0 },
  { year: '2023', mba: 70.3, dba: 82.5, institution: 99.2 },
  { year: '2024', mba: 100.0, dba: 85.9, institution: 99.7 },
  { year: '2025', mba: 97.3, dba: 94.4, institution: 99.7 },
];

/** Institution-wide graduates per year (all programmes). */
export interface GraduatesRow {
  year: string;
  count: number;
}

export const GRADUATES_SERIES: readonly GraduatesRow[] = [
  { year: '2021', count: 319 },
  { year: '2022', count: 498 },
  { year: '2023', count: 792 },
  { year: '2024', count: 925 },
  { year: '2025', count: 1275 },
];

/**
 * ACBSP learning-outcome achievement, MBA & DBA, 2022–2025 (% of students
 * meeting the outcome). A dash in the source (DBA 2022) is represented as null.
 */
export interface LearningOutcome {
  outcome: string;
  mba2025: number;
  dba2025: number;
}

export const LEARNING_OUTCOMES: readonly LearningOutcome[] = [
  { outcome: 'Autonomy & independent thinking', mba2025: 87.63, dba2025: 89.12 },
  { outcome: 'Responsibility for continuous learning', mba2025: 87.03, dba2025: 88.8 },
];

/* ── Full-detail series (for the dedicated /experience/student-achievement-rates
 * page). Every figure below is reproduced exactly from the authoritative ACBSP
 * page (see ACHIEVEMENT_SOURCE_URL), 2021–2025. ── */

/** ACBSP MBA & DBA graduate counts per year (accredited programmes only). */
export interface AcbspGraduatesRow {
  year: string;
  mba: number;
  dba: number;
}

export const ACBSP_GRADUATES_SERIES: readonly AcbspGraduatesRow[] = [
  { year: '2021', mba: 61, dba: 1 },
  { year: '2022', mba: 22, dba: 8 },
  { year: '2023', mba: 31, dba: 12 },
  { year: '2024', mba: 49, dba: 9 },
  { year: '2025', mba: 3, dba: 6 },
];

/**
 * One ACBSP learning outcome tracked year-by-year for MBA & DBA (% of students
 * meeting it). `null` = not reported that year (DBA has no 2022 figure).
 */
export interface OutcomeTrendPoint {
  year: string;
  mba: number | null;
  dba: number | null;
}

export interface LearningOutcomeTrend {
  outcome: string;
  series: readonly OutcomeTrendPoint[];
}

export const LEARNING_OUTCOME_TRENDS: readonly LearningOutcomeTrend[] = [
  {
    outcome: 'Autonomy & independent thinking',
    series: [
      { year: '2022', mba: 83.26, dba: null },
      { year: '2023', mba: 85.27, dba: 80.59 },
      { year: '2024', mba: 88.26, dba: 90.05 },
      { year: '2025', mba: 87.63, dba: 89.12 },
    ],
  },
  {
    outcome: 'Responsibility for continuous learning',
    series: [
      { year: '2022', mba: 83.72, dba: null },
      { year: '2023', mba: 86.46, dba: 78.86 },
      { year: '2024', mba: 88.32, dba: 89.47 },
      { year: '2025', mba: 87.03, dba: 88.8 },
    ],
  },
];

/** Where the full, authoritative figures live (mandated dedicated page). */
export const ACHIEVEMENT_SOURCE_URL =
  'https://ibas.edu.eu/student-support/students-achievement-rates/';

/* ═══════════════════════════════════════════════════════════════════════════
 * 3 · ANDRAGOGICAL PRINCIPLES ★  — VERBATIM, accreditation-mandated (Appendix C).
 *     The heading and the five principle texts MUST NOT be reworded.
 * ═══════════════════════════════════════════════════════════════════════════ */

export const ANDRAGOGY_HEADING = 'At IBAS, We Apply the Andragogy Principle';

export interface AndragogyPrinciple {
  /** Principle name (verbatim). */
  title: string;
  /** Principle text — reproduced verbatim from Appendix C. */
  body: string;
  Icon: LucideIcon;
}

export const ANDRAGOGY_PRINCIPLES: readonly AndragogyPrinciple[] = [
  {
    title: 'Self-Concept',
    body: 'As a person matures, his self-concept moves from one of being a dependent personality toward one of being a self-directed human being.',
    Icon: Compass,
  },
  {
    title: 'Experience',
    body: 'As a person matures he accumulates a growing reservoir of experience that becomes an increasing resource for learning.',
    Icon: BookOpen,
  },
  {
    title: 'Readiness to Learn',
    body: 'As a person matures his readiness to learn becomes oriented increasingly to the developmental tasks of his social roles.',
    Icon: Target,
  },
  {
    title: 'Orientation to Learning',
    body: 'As a person matures his time perspective changes from one of postponed application of knowledge to immediacy of application, and accordingly, his orientation toward learning shifts from one of subject-centeredness to one of problem-centredness.',
    Icon: Lightbulb,
  },
  {
    title: 'Motivation to Learn',
    body: 'As a person matures the motivation to learn is internal.',
    Icon: Brain,
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 4 · INTERNSHIP  (source: /ibas-experience/internship/)
 *     No placement is guaranteed — the disclaimer is mandatory.
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface InternshipPathway {
  name: string;
  /** Who it is open to. */
  audience: string;
  description: string;
  /** Short supporting points. */
  points: readonly string[];
  Icon: LucideIcon;
}

export const INTERNSHIP_PATHWAYS: readonly InternshipPathway[] = [
  {
    name: 'Remote internships',
    audience: 'Open to online programme students',
    description:
      'Work with international companies while keeping the location flexibility of online study.',
    points: [
      'Develop digital leadership skills',
      'Apply academic knowledge in practice',
      'Strengthen your professional profile',
    ],
    Icon: Laptop,
  },
  {
    name: 'Global internships',
    audience: 'Open to all IBAS students',
    description:
      'Join the IBAS Global Internship Programme — with partnerships offering placements in New York, London and Singapore — or secure an independent international placement while you study online.',
    points: [
      'Placements in New York, London and Singapore',
      'Or arrange an independent international placement',
      'Study online while gaining work experience',
    ],
    Icon: Building2,
  },
  {
    name: 'On-site internships',
    audience: 'Open to eligible students, in Switzerland',
    description:
      'Available at undergraduate level for hospitality and business students, and included annually in the two-year master’s programmes.',
    points: [
      'German or French proficiency required for most positions',
      'Undergraduate hospitality & business',
      'Included annually in two-year master’s programmes',
    ],
    Icon: MapPin,
  },
];

/**
 * Mandatory disclaimer — reproduced from the source page. Remote internships
 * carry additional fees; placements are never guaranteed.
 */
export const INTERNSHIP_DISCLAIMER =
  'Remote internships are subject to additional fees. IBAS does not guarantee internship placements.';

/* ═══════════════════════════════════════════════════════════════════════════
 * 5 · ALUMNI  (source: /ibas-experience/alumni/)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const ALUMNI_LEDE =
  'The IBAS alumni community connects graduates who share a common academic foundation and an international perspective on business and leadership.';

/** Hero film for the alumni section — a real IBAS graduate testimonial (subtitled). */
export const ALUMNI_HERO = {
  video: '/media/videos/chinese-graduate-testimonial-subtitled.mp4',
  poster: '/media/videos/posters/chinese-graduate-testimonial-subtitled.webp',
  alt: 'An IBAS graduate in cap and gown holding her degree at the graduation ceremony, from a subtitled testimonial film.',
} as const;

/** The pull-quote that frames the alumni relationship (verbatim from source). */
export const ALUMNI_QUOTE =
  'Graduation is not the end of the relationship — it is the beginning of a lasting professional connection.';

export interface AlumniPillar {
  title: string;
  description: string;
  Icon: LucideIcon;
}

export const ALUMNI_PILLARS: readonly AlumniPillar[] = [
  {
    title: 'Graduation & recognition',
    description:
      'Formal graduation ceremonies celebrate your achievement, with access to a graduate verification platform for degree confirmation.',
    Icon: GraduationCap,
  },
  {
    title: 'Guest speaking & exchange',
    description:
      'Alumni are occasionally invited to serve as guest speakers at on-site academic activities in Zurich, sharing their professional experience with current students.',
    Icon: Presentation,
  },
  {
    title: 'Global networking',
    description:
      'IBAS collaborates with My Global World (MGW) to provide international visibility and optional networking opportunities for graduates.',
    Icon: Handshake,
  },
];

/** A photograph shown on the spotlight card — a verified ibas.edu.eu asset. */
export interface SpotlightPhoto {
  src: string;
  alt: string;
}

/**
 * A featured alumna named on the source page. Only verified facts — she is MBA
 * Class of 2018 and now a business owner in Switzerland (per the news story).
 * `href` points at the full guest-speaker story on /news; both photos are from
 * her 29 April 2025 campus visit (curl-verified ibas.edu.eu originals — no
 * verified photo of her 2018 graduation exists in the media library or the
 * old-site archive, so graduation imagery is deliberately not shown).
 */
export const ALUMNI_SPOTLIGHT = {
  name: 'Sirithakorn Srinitioj (“Chisa”)',
  note: 'MBA Class of 2018 — now a business owner in Switzerland, she returned to campus as a guest speaker to share her professional journey with current students.',
  href: '/news/thank-you-to-ibas-alumna-sirithakorn-srinitioj-nickname-chisa-for-joining-us-as-a-guest-speaker',
  cta: 'Read the story',
  photos: [
    {
      src: 'https://ibas.edu.eu/wp-content/uploads/2025/05/20250429_1051520-800x1067.jpg',
      alt: 'Sirithakorn Srinitioj (Chisa) smiling with both thumbs up in front of the IBAS display shelf during her guest-speaker visit.',
    },
    {
      src: 'https://ibas.edu.eu/wp-content/uploads/2025/05/IMG20250429101630-800x600.jpg',
      alt: 'Chisa speaking beside a whiteboard to current IBAS students during her guest-speaker session.',
    },
  ],
} as const satisfies {
  name: string;
  note: string;
  href: string;
  cta: string;
  photos: readonly SpotlightPhoto[];
};

/**
 * Real graduate testimonials (homepage / content bank — attributed, verbatim).
 * US spelling in these quotes is the speakers’ own and is left unaltered.
 */
export interface AlumniTestimonial {
  quote: string;
  name: string;
  detail: string;
}

export const ALUMNI_TESTIMONIALS: readonly AlumniTestimonial[] = [
  {
    quote:
      'At IBAS, the dedicated lecturer and staff helped me to meet this challenge by fortifying the program with mentorship, and constant encouragement.',
    name: 'Dr. Rozaidi Razali',
    detail: 'Malaysia',
  },
  {
    quote:
      'The Doctorate of Business Administration program also affected my personal life, resulting in improved personal skills in terms of analyzing, criticizing, and decision-making.',
    name: 'Dr. Roy Prasad',
    detail: 'Malaysia',
  },
  {
    quote:
      'It was an excellent experience studying with IBAS. The perfect combination of great professors and flexible hours of teaching helped my achievement.',
    name: 'Dr. Prem Kumar',
    detail: 'Malaysia',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 6 · GALLERY  — real IBAS media library imagery.
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface GalleryItem {
  src: string;
  alt: string;
  caption: string;
  /** Tailwind span hint for the mosaic; 'wide' | 'tall' | 'normal'. */
  span?: 'wide' | 'tall' | 'normal';
}

export const GALLERY_ITEMS: readonly GalleryItem[] = [
  {
    src: '/media/images/campus/classroom-small-group-discussion.webp',
    alt: 'Instructor guiding a small group discussion among students in a classroom.',
    caption: 'Collaborative study',
    span: 'wide',
  },
  {
    src: '/media/images/campus/ibas-classroom-lecture-session.webp',
    alt: 'Students and an instructor in an IBAS classroom during a lecture with a projected slide.',
    caption: 'Academic sessions',
    span: 'tall',
  },
  {
    src: '/media/images/campus/ibas-thalwil-building-exterior.webp',
    alt: 'Exterior view of the copper-panelled IBAS Thalwil campus building.',
    caption: 'On-site in Thalwil',
    span: 'normal',
  },
  {
    src: '/media/images/campus/campus-lounge-sofas.webp',
    alt: 'Lounge area with grey sofas and orange cushions overlooking large windows.',
    caption: 'Lounge & discussion',
    span: 'normal',
  },
  {
    src: '/media/images/graduation-events/graduation-ceremony-group-celebration.webp',
    alt: 'Large group of MBA and DBA graduates in gowns and mortarboards celebrating together at an IBAS graduation ceremony.',
    caption: 'Graduation & community',
    span: 'wide',
  },
];
